// src/components/RoomAllocation.js
import React, { useState, useEffect, useRef } from 'react';

export default function RoomAllocation({ onAllocate }) {
  const [selectedRoom, setSelectedRoom] = useState('');
  const [rooms, setRooms] = useState([
    { id: '101', number: '101', floor: 1, type: 'Deluxe', status: 'vacant', allocated: false, allocatedTo: null, lastCleaned: '2024-01-15T10:30:00', approvedBy: null, approvedAt: null },
    { id: '102', number: '102', floor: 1, type: 'Standard', status: 'occupied', allocated: true, allocatedTo: 'John Doe', lastCleaned: '2024-01-14T14:20:00', approvedBy: 'Admin', approvedAt: '2024-01-14T10:00:00' },
    { id: '103', number: '103', floor: 1, type: 'Standard', status: 'vacant', allocated: false, allocatedTo: null, lastCleaned: '2024-01-15T09:15:00', approvedBy: null, approvedAt: null },
    { id: '104', number: '104', floor: 1, type: 'Deluxe', status: 'maintenance', allocated: false, allocatedTo: null, lastCleaned: '2024-01-10T11:00:00', approvedBy: null, approvedAt: null },
    { id: '105', number: '105', floor: 1, type: 'Suite', status: 'vacant', allocated: false, allocatedTo: null, lastCleaned: '2024-01-15T08:45:00', approvedBy: null, approvedAt: null },
    { id: '201', number: '201', floor: 2, type: 'Deluxe', status: 'cleaning', allocated: true, allocatedTo: 'Sarah Smith', lastCleaned: null, approvedBy: 'Manager', approvedAt: '2024-01-13T14:30:00' },
    { id: '202', number: '202', floor: 2, type: 'Standard', status: 'vacant', allocated: false, allocatedTo: null, lastCleaned: '2024-01-14T16:30:00', approvedBy: null, approvedAt: null },
    { id: '203', number: '203', floor: 2, type: 'Standard', status: 'occupied', allocated: true, allocatedTo: 'Mike Johnson', lastCleaned: '2024-01-13T12:00:00', approvedBy: 'Admin', approvedAt: '2024-01-13T11:00:00' },
    { id: '204', number: '204', floor: 2, type: 'Deluxe', status: 'vacant', allocated: false, allocatedTo: null, lastCleaned: '2024-01-15T07:30:00', approvedBy: null, approvedAt: null },
    { id: '205', number: '205', floor: 2, type: 'Suite', status: 'vacant', allocated: false, allocatedTo: null, lastCleaned: '2024-01-14T15:45:00', approvedBy: null, approvedAt: null },
  ]);
  
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [qrRoomId, setQrRoomId] = useState('');
  const [scanningRoom, setScanningRoom] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [selectedRoomDetails, setSelectedRoomDetails] = useState(null);
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingRoom, setPendingRoom] = useState(null);
  const [approvalData, setApprovalData] = useState({
    personName: '',
    approverName: '',
    approvedDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [newRoom, setNewRoom] = useState({
    floor: 1,
    roomNumber: '',
    type: 'Standard',
    status: 'vacant'
  });
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Room types available
  const roomTypes = ['Standard', 'Deluxe', 'Suite', 'Penthouse', 'Economy'];

  // Get status badge styling
  const getStatusBadge = (status) => {
    const badges = {
      vacant: { label: 'Vacant', class: 'bg-green-100 text-green-700', icon: '✅' },
      occupied: { label: 'Occupied', class: 'bg-blue-100 text-blue-700', icon: '👤' },
      cleaning: { label: 'Cleaning', class: 'bg-yellow-100 text-yellow-700', icon: '🧹' },
      maintenance: { label: 'Maintenance', class: 'bg-red-100 text-red-700', icon: '🔧' },
      allocated: { label: 'Allocated', class: 'bg-purple-100 text-purple-700', icon: '🔑' },
      pending: { label: 'Pending Approval', class: 'bg-orange-100 text-orange-700', icon: '⏳' },
    };
    return badges[status] || { label: status, class: 'bg-gray-100 text-gray-700', icon: '📋' };
  };

  // Generate unique room ID
  const generateRoomId = (floor, roomNumber) => {
    return `${floor}${roomNumber.padStart(2, '0')}`;
  };

  // Check if room number already exists
  const isRoomNumberExists = (floor, roomNumber) => {
    const roomId = generateRoomId(floor, roomNumber);
    return rooms.some(room => room.id === roomId);
  };

  // Add new room
  const handleAddRoom = () => {
    if (!newRoom.roomNumber) {
      setCheckInStatus({ success: false, message: 'Please enter room number' });
      setTimeout(() => setCheckInStatus(null), 3000);
      return;
    }

    const roomNumber = newRoom.roomNumber.padStart(2, '0');
    if (isRoomNumberExists(newRoom.floor, roomNumber)) {
      setCheckInStatus({ success: false, message: `Room ${newRoom.floor}${roomNumber} already exists!` });
      setTimeout(() => setCheckInStatus(null), 3000);
      return;
    }

    const newRoomData = {
      id: generateRoomId(newRoom.floor, roomNumber),
      number: `${newRoom.floor}${roomNumber}`,
      floor: newRoom.floor,
      type: newRoom.type,
      status: 'pending',
      allocated: false,
      allocatedTo: null,
      lastCleaned: null,
      approvedBy: null,
      approvedAt: null,
      createdAt: new Date().toISOString()
    };

    setRooms([...rooms, newRoomData]);
    setShowAddRoomModal(false);
    setNewRoom({ floor: 1, roomNumber: '', type: 'Standard', status: 'vacant' });
    setCheckInStatus({ success: true, message: `Room ${newRoomData.number} added successfully! Pending approval.` });
    setTimeout(() => setCheckInStatus(null), 3000);
  };

  // Open approval modal for room
  const openApprovalModal = (room) => {
    setPendingRoom(room);
    setApprovalData({
      personName: '',
      approverName: '',
      approvedDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowApprovalModal(true);
  };

  // Approve room and assign to person
  const handleApproveRoom = () => {
    if (!approvalData.personName || !approvalData.approverName) {
      setCheckInStatus({ success: false, message: 'Please enter person name and approver name' });
      setTimeout(() => setCheckInStatus(null), 3000);
      return;
    }

    const updatedRooms = rooms.map(room =>
      room.id === pendingRoom.id
        ? {
            ...room,
            status: 'allocated',
            allocated: true,
            allocatedTo: approvalData.personName,
            approvedBy: approvalData.approverName,
            approvedAt: new Date().toISOString(),
            approvalNotes: approvalData.notes
          }
        : room
    );

    setRooms(updatedRooms);
    setShowApprovalModal(false);
    setPendingRoom(null);
    
    const newAllocation = {
      roomId: pendingRoom.id,
      roomNumber: pendingRoom.number,
      personName: approvalData.personName,
      approverName: approvalData.approverName,
      timestamp: new Date().toISOString(),
      status: 'allocated'
    };
    
    if (onAllocate) onAllocate(newAllocation);
    
    setCheckInStatus({ success: true, message: `Room ${pendingRoom.number} approved and allocated to ${approvalData.personName}` });
    setTimeout(() => setCheckInStatus(null), 3000);
  };

  // Handle room allocation
  const handleAllocate = () => {
    if (selectedRoom) {
      const room = rooms.find(r => r.id === selectedRoom);
      if (room && room.status === 'vacant') {
        openApprovalModal(room);
      } else {
        setCheckInStatus({ success: false, message: `Room ${room?.number} is not available for allocation` });
        setTimeout(() => setCheckInStatus(null), 3000);
      }
    }
  };

  // Open QR scanner for check-in
  const openQrScanner = (room) => {
    setScanningRoom(room);
    setQrScannerOpen(true);
    setQrRoomId('');
    
    setTimeout(() => {
      setQrRoomId(room.number);
    }, 2000);
  };

  // Handle QR check-in
  const handleQrCheckIn = () => {
    if (qrRoomId && scanningRoom) {
      const room = rooms.find(r => r.id === scanningRoom.id);
      if (room && room.status === 'allocated') {
        const updatedRooms = rooms.map(r => 
          r.id === scanningRoom.id 
            ? { ...r, status: 'cleaning', lastCleaned: new Date().toISOString() }
            : r
        );
        setRooms(updatedRooms);
        setCheckInStatus({ success: true, message: `Checked into Room ${room.number} successfully!` });
        setQrScannerOpen(false);
        setScanningRoom(null);
      } else {
        setCheckInStatus({ success: false, message: `Cannot check into Room ${room?.number}. Invalid status.` });
      }
      setTimeout(() => setCheckInStatus(null), 3000);
    }
  };

  // View room details
  const viewRoomDetails = (room) => {
    setSelectedRoomDetails(room);
  };

  // Get room status color for visual indicator
  const getRoomColor = (status) => {
    switch(status) {
      case 'vacant': return 'border-green-500 bg-green-50';
      case 'occupied': return 'border-blue-500 bg-blue-50';
      case 'cleaning': return 'border-yellow-500 bg-yellow-50';
      case 'maintenance': return 'border-red-500 bg-red-50';
      case 'allocated': return 'border-purple-500 bg-purple-50';
      case 'pending': return 'border-orange-500 bg-orange-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  // Group rooms by floor
  const roomsByFloor = rooms.reduce((acc, room) => {
    if (!acc[room.floor]) acc[room.floor] = [];
    acc[room.floor].push(room);
    return acc;
  }, {});

  // Get floors 1-10
  const floors = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Room Allocation & Check-in System</h2>
        <p className="text-sm text-gray-500 mt-1">Manage room assignments, approvals, and QR code check-in process</p>
      </div>

      {/* Status Message */}
      {checkInStatus && (
        <div className={`mb-4 p-3 rounded-lg ${checkInStatus.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {checkInStatus.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Room Grid */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-gray-800">Room Selection</h3>
                <p className="text-xs text-gray-500 mt-1">Click on any room to allocate or check-in</p>
              </div>
              <button
                onClick={() => setShowAddRoomModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition flex items-center gap-1"
              >
                + Add New Room
              </button>
            </div>
            
            <div className="p-4 max-h-[600px] overflow-y-auto">
              {floors.map(floor => (
                <div key={floor} className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-semibold text-gray-700">🏢 Floor {floor}</span>
                    <span className="text-xs text-gray-400">({roomsByFloor[floor]?.length || 0} rooms)</span>
                  </div>
                  {roomsByFloor[floor] ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {roomsByFloor[floor].map(room => {
                        const statusBadge = getStatusBadge(room.status);
                        return (
                          <div
                            key={room.id}
                            className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${getRoomColor(room.status)} hover:shadow-md`}
                            onClick={() => setSelectedRoom(room.id)}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <span className="font-bold text-lg text-gray-800">{room.number}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge.class}`}>
                                {statusBadge.icon} {statusBadge.label}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div>{room.type}</div>
                              {room.allocatedTo && <div className="text-gray-500">👤 {room.allocatedTo}</div>}
                              {room.approvedBy && <div className="text-gray-400">✓ Approved by: {room.approvedBy}</div>}
                              {room.lastCleaned && (
                                <div className="text-gray-400">
                                  🕐 Cleaned: {new Date(room.lastCleaned).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                            {selectedRoom === room.id && (
                              <div className="mt-2 text-xs text-blue-600 font-medium">✓ Selected</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-lg">
                      No rooms on floor {floor}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Actions */}
        <div className="space-y-4">
          {/* Allocation Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span>🔑</span> Room Allocation
            </h3>
            {selectedRoom ? (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Selected Room: <span className="font-bold text-blue-600">{selectedRoom}</span></p>
                  {rooms.find(r => r.id === selectedRoom)?.status === 'vacant' ? (
                    <p className="text-xs text-green-600 mt-1">✓ Available for allocation</p>
                  ) : rooms.find(r => r.id === selectedRoom)?.status === 'pending' ? (
                    <p className="text-xs text-orange-600 mt-1">⏳ Pending approval</p>
                  ) : (
                    <p className="text-xs text-red-600 mt-1">⚠️ Room not available</p>
                  )}
                </div>
                <button
                  onClick={handleAllocate}
                  disabled={rooms.find(r => r.id === selectedRoom)?.status !== 'vacant'}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Request Allocation for Room {selectedRoom}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Select a room from the grid to allocate</p>
            )}
          </div>

          {/* QR Check-in Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span>📱</span> QR Code Check-in
            </h3>
            <p className="text-xs text-gray-500 mb-3">Scan QR code to check-in to allocated room</p>
            
            {rooms.filter(r => r.status === 'allocated').length > 0 ? (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {rooms.filter(r => r.status === 'allocated').map(room => (
                  <div key={room.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">Room {room.number}</span>
                      <span className="text-xs text-gray-500 ml-2">Allocated to: {room.allocatedTo}</span>
                    </div>
                    <button
                      onClick={() => openQrScanner(room)}
                      className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition"
                    >
                      📷 Scan QR
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-3">No allocated rooms pending check-in</p>
            )}
          </div>

          {/* Room Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Room Status Summary</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center p-2 bg-green-50 rounded">
                <div className="text-lg font-bold text-green-600">{rooms.filter(r => r.status === 'vacant').length}</div>
                <div className="text-xs text-gray-600">Vacant</div>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded">
                <div className="text-lg font-bold text-blue-600">{rooms.filter(r => r.status === 'occupied').length}</div>
                <div className="text-xs text-gray-600">Occupied</div>
              </div>
              <div className="text-center p-2 bg-yellow-50 rounded">
                <div className="text-lg font-bold text-yellow-600">{rooms.filter(r => r.status === 'cleaning').length}</div>
                <div className="text-xs text-gray-600">Cleaning</div>
              </div>
              <div className="text-center p-2 bg-purple-50 rounded">
                <div className="text-lg font-bold text-purple-600">{rooms.filter(r => r.status === 'allocated').length}</div>
                <div className="text-xs text-gray-600">Allocated</div>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded col-span-2">
                <div className="text-lg font-bold text-orange-600">{rooms.filter(r => r.status === 'pending').length}</div>
                <div className="text-xs text-gray-600">Pending Approval</div>
              </div>
            </div>
          </div>

          {/* View Details Button for Selected Room */}
          {selectedRoom && (
            <button
              onClick={() => viewRoomDetails(rooms.find(r => r.id === selectedRoom))}
              className="w-full py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              View Room Details
            </button>
          )}
        </div>
      </div>

      {/* Add Room Modal */}
      {showAddRoomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Room</h3>
              <button onClick={() => setShowAddRoomModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Floor (1-10)</label>
                <select
                  value={newRoom.floor}
                  onChange={(e) => setNewRoom({ ...newRoom, floor: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {floors.map(floor => (
                    <option key={floor} value={floor}>Floor {floor}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Number (01-99)</label>
                <input
                  type="number"
                  min="1"
                  max="99"
                  value={newRoom.roomNumber}
                  onChange={(e) => setNewRoom({ ...newRoom, roomNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter room number"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                <select
                  value={newRoom.type}
                  onChange={(e) => setNewRoom({ ...newRoom, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {roomTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleAddRoom}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Room
                </button>
                <button
                  onClick={() => setShowAddRoomModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && pendingRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Approve Room Allocation</h3>
              <button onClick={() => setShowApprovalModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm">Room Number: <span className="font-bold">{pendingRoom.number}</span></p>
                <p className="text-sm">Room Type: <span className="font-bold">{pendingRoom.type}</span></p>
                <p className="text-sm">Floor: <span className="font-bold">{pendingRoom.floor}</span></p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Person Name *</label>
                <input
                  type="text"
                  value={approvalData.personName}
                  onChange={(e) => setApprovalData({ ...approvalData, personName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter person name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Approver Name *</label>
                <input
                  type="text"
                  value={approvalData.approverName}
                  onChange={(e) => setApprovalData({ ...approvalData, approverName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter approver name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Approval Date</label>
                <input
                  type="date"
                  value={approvalData.approvedDate}
                  onChange={(e) => setApprovalData({ ...approvalData, approvedDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={approvalData.notes}
                  onChange={(e) => setApprovalData({ ...approvalData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Any additional notes..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleApproveRoom}
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Approve & Allocate
                </button>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Scanner Modal */}
      {qrScannerOpen && scanningRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Scan QR Code</h3>
              <button onClick={() => setQrScannerOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            
            <div className="text-center">
              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <div className="w-48 h-48 mx-auto border-2 border-dashed border-gray-400 rounded-lg flex items-center justify-center">
                  {qrRoomId ? (
                    <div className="text-center">
                      <div className="text-4xl mb-2">✅</div>
                      <div className="text-sm font-mono bg-white p-2 rounded">Room {qrRoomId}</div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="animate-pulse text-4xl mb-2">📷</div>
                      <div className="text-sm text-gray-500">Scanning...</div>
                    </div>
                  )}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Scanning QR code for Room {scanningRoom.number}
              </p>
              
              {qrRoomId && (
                <button
                  onClick={handleQrCheckIn}
                  className="w-full py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition"
                >
                  Confirm Check-in
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Room Details Modal */}
      {selectedRoomDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedRoomDetails(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Room {selectedRoomDetails.number} Details</h3>
              <button onClick={() => setSelectedRoomDetails(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Room Type:</span>
                <span className="font-medium">{selectedRoomDetails.type}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Floor:</span>
                <span className="font-medium">Floor {selectedRoomDetails.floor}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium px-2 py-0.5 rounded-full ${getStatusBadge(selectedRoomDetails.status).class}`}>
                  {getStatusBadge(selectedRoomDetails.status).label}
                </span>
              </div>
              {selectedRoomDetails.allocatedTo && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Allocated To:</span>
                  <span className="font-medium">{selectedRoomDetails.allocatedTo}</span>
                </div>
              )}
              {selectedRoomDetails.approvedBy && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Approved By:</span>
                  <span className="font-medium">{selectedRoomDetails.approvedBy}</span>
                </div>
              )}
              {selectedRoomDetails.approvedAt && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Approved Date:</span>
                  <span className="font-medium">{new Date(selectedRoomDetails.approvedAt).toLocaleString()}</span>
                </div>
              )}
              {selectedRoomDetails.lastCleaned && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Last Cleaned:</span>
                  <span className="font-medium">{new Date(selectedRoomDetails.lastCleaned).toLocaleString()}</span>
                </div>
              )}
              {selectedRoomDetails.approvalNotes && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Notes:</span>
                  <span className="font-medium">{selectedRoomDetails.approvalNotes}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Last Updated:</span>
                <span className="font-medium">{new Date().toLocaleTimeString()}</span>
              </div>
            </div>
            
            <div className="mt-6 flex gap-3">
              {selectedRoomDetails.status === 'vacant' && (
                <button
                  onClick={() => {
                    openApprovalModal(selectedRoomDetails);
                    setSelectedRoomDetails(null);
                  }}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Request Allocation
                </button>
              )}
              {selectedRoomDetails.status === 'allocated' && (
                <button
                  onClick={() => {
                    openQrScanner(selectedRoomDetails);
                    setSelectedRoomDetails(null);
                  }}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Scan QR to Check-in
                </button>
              )}
              {selectedRoomDetails.status === 'pending' && (
                <button
                  onClick={() => {
                    openApprovalModal(selectedRoomDetails);
                    setSelectedRoomDetails(null);
                  }}
                  className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Review & Approve
                </button>
              )}
              <button onClick={() => setSelectedRoomDetails(null)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}